// =====================================================================
// TIER ENTITLEMENTS — single source of truth for the 5-tier model.
// =====================================================================
// Public ladder: Starter → Plus → Pro → Elite → Agency.
// `comp` and `admin_workspace` are internal grants.
//
// Every tier-sensitive decision in the app — feature gating, usage caps,
// workspace counts, publishing connector counts, AI bundle access,
// Topic Tree caps, sitewide voice availability, affiliate eligibility —
// must read from TIER_ENTITLEMENTS below. Do not hardcode tier checks
// elsewhere.
// =====================================================================

export type Tier =
  | "starter"
  | "plus"
  | "pro"
  | "elite"
  | "agency"
  | "comp"
  | "admin_workspace"
  | "super_admin";

export type Feature = "custom_color" | "brand_color" | "gradient" | "gradient_preset" | "overlay";

export type Capability =
  | "custom_color"
  | "brand_color"
  | "gradient"
  | "gradient_preset"
  | "overlay"
  | "readwise"
  | "composer_access"
  | "content_frameworks"
  | "content_avatars"
  | "content_sources"
  | "content_briefs"
  | "content_reference_mentions"
  | "content_planning_agent"
  | "content_writing_agent"
  | "content_publishing_prep"
  | "content_publishing_dispatch"
  | "content_multi_workspace"
  // Post image generator BYOK (Fal.ai). Retained for the upcoming
  // Post Image generator (Beta). Lower tiers see the card but cannot
  // save a key.
  | "content_image_provider_byok"
  // MCP Inbound — Excerptus exposes its own MCP server so Claude.ai and
  // other MCP clients can read/write a workspace via PATs. Plus and above.
  // See mem://features/mcp-server.md.
  | "mcp_inbound"
  // MCP Outbound — Composer AI Chat connects out to user-added external
  // MCP servers (Notion, Linear, n8n, custom). Plus and above.
  // Per-workspace numeric cap lives in `mcpOutboundPerWorkspace`.
  | "mcp_outbound"
  // Team members / VA invites. Elite + Agency only. Numeric caps live in
  // `teamSeatsMax` (account-wide) and `teamSeatsPerWorkspace`.
  | "team_members"
  // @deprecated Use `mcp_inbound` (kept as alias for back-compat).
  | "mcp_access";

// AI bundle tiers — names of the inference paths a tier can reach.
// - none: no AI access
// - lovable_basic_metered: Lovable AI only, capped by a small monthly token allowance
//                          (Starter — non-premium jobs only, premium writing blocked)
// - lovable_basic: Lovable AI only (legacy)
// - lovable_plus_premium_capped: Lovable AI + capped premium-writing model access
// - lovable_plus_premium: Lovable AI + uncapped premium-writing path
// - byok_required: BYOK required for all inference (Elite, Agency)
// - lovable_plus_premium_byok: legacy admin path (full access + BYOK)
export type AiBundle =
  | "none"
  | "lovable_basic_metered"
  | "lovable_basic"
  | "lovable_plus_premium_capped"
  | "lovable_plus_premium"
  | "byok_required"
  | "lovable_plus_premium_byok";

// Starter monthly token allowance (Excerptus AI metered tier).
// `null` means unlimited. Numbers are hard caps for the workspace/account.
export type TierEntitlements = {
  // Usage caps (per primary workspace, per month where applicable).
  excerpts: number | null;
  downloadsPerMonth: number | null;
  // Plan-level workspace count (base count, before add-ons).
  workspaces: number | null;
  // How many additional workspaces can be purchased as add-ons.
  // 0 = no add-ons allowed. null = unlimited add-ons.
  workspaceAddOnMax: number | null;
  // Plan-level publishing connector count across destinations.
  publishingConnectors: number | null;
  // Which AI inference paths this tier can use.
  aiBundle: AiBundle;
  // Topic Tree (Framework) per-workspace caps.
  topicTreeColumnCap: number;
  topicsPerColumnCap: number;
  // Monthly Lovable AI credit allowance for this tier (account-wide, resets
  // on the 1st). null = no shared allowance (e.g. Elite/Agency are BYOK,
  // but they can still buy top-up credits for non-BYOK paths).
  monthlyAiCredits: number | null;
  // Whether the tier is eligible to participate in the affiliate program
  // at all (runtime gating still requires has-ever-paid + currently-active).
  affiliateAvailable: boolean;
  // Per-workspace cap on RSS/Atom feed subscriptions in My Feed Reader.
  // Applies per workspace (not per account) — mirrors how library/excerpt
  // caps work. The cap reflects one human's reading bandwidth and does not
  // scale with workspace count. See mem://features/my-feed-reader.md.
  feedSubscriptionsMax: number;
  // Per-workspace cap on My Podcasts subscriptions. Mirrors feedSubscriptionsMax
  // exactly — same per-human bandwidth reasoning.
  // See mem://features/podcasts-v1-spec.md.
  podcastSubscriptionsMax: number;
  // Per-workspace daily cap on on-open full-article extractions.
  // Runaway-protection only — a normal reader will never approach this.
  // Mirrored server-side in supabase/functions/_shared/extractionLimits.ts.
  feedExtractionsPerDay: number;
  // Per-workspace cap on My Corpus documents. null = unlimited.
  // Corpus RAG is intentionally ungated; this cap is the abuse-control lever.
  corpusDocumentCap: number | null;
  // Per-workspace byte cap on the Workspace Media image store.
  // null = unlimited (internal tiers only). See mem://features/workspace-media.md.
  workspaceMediaBytes: number | null;
  // Per-workspace cap on outbound MCP connections (Composer → external MCP
  // servers). 0 = feature off. Flat 10 across all paid tiers today.
  mcpOutboundPerWorkspace: number;
  // My Library source caps. Exactly one of these is set per paid tier (the
  // other is null). Internal tiers (comp/admin/super_admin) have both null
  // = unlimited. Mirrored server-side in the `library_items_tier_caps`
  // table — keep them in sync.
  libraryItemsAccountCap: number | null;
  libraryItemsPerWorkspaceCap: number | null;
  // Team-member seats (active + pending invites) across all owned
  // workspaces. null = unlimited; 0 = feature disabled. See
  // mem://features/workspace-members.md.
  teamSeatsMax: number | null;
  // Per-workspace cap on team-member seats. null = unlimited; 0 = no
  // team members on this tier.
  teamSeatsPerWorkspace: number | null;
  // Capability flags (true = available on this tier).
  capabilities: Record<Capability, boolean>;
};

// Workspace Media byte caps (per-workspace, live rows only).
// Real numbers — do not paraphrase. See mem://features/workspace-media.md.
const MB = 1024 * 1024;
const GB = 1024 * MB;
export const WORKSPACE_MEDIA_BYTES_BY_TIER = {
  starter: 100 * MB,
  plus: 500 * MB,
  pro: 2 * GB,
  elite: 10 * GB,
  agency: 25 * GB,
} as const;

// Capability ordering helpers. Kept private — callers should ask the
// entitlement table, not reason about tier hierarchy directly.
const PLUS_OR_HIGHER: readonly Tier[] = ["plus", "pro", "elite", "agency", "comp", "admin_workspace", "super_admin"];
const PRO_OR_HIGHER: readonly Tier[] = ["pro", "elite", "agency", "comp", "admin_workspace", "super_admin"];

const TEAM_MEMBER_TIERS: readonly Tier[] = ["elite", "agency", "comp", "admin_workspace", "super_admin"];

const cap = (tier: Tier): Record<Capability, boolean> => {
  const plusPlus = PLUS_OR_HIGHER.includes(tier);
  const proPlus = PRO_OR_HIGHER.includes(tier);
  return {
    // Excerpt Quote Image Generator design features are ungated for every tier.
    // Only `downloadsPerMonth` differentiates tiers for the generator.
    custom_color: true,
    brand_color: true,
    gradient: true,
    gradient_preset: true,
    overlay: true,
    // Readwise sync: ungated — available on every tier.
    readwise: true,
    // Composer: available on EVERY tier (Starter through Agency).
    // Do not re-introduce a tier gate here — see mem://features/composer-access-policy.md.
    composer_access: true,
    content_frameworks: true,
    content_avatars: true,
    content_sources: true,
    content_briefs: true,
    content_reference_mentions: true,
    content_planning_agent: true,
    content_writing_agent: true,
    content_publishing_prep: true,
    content_publishing_dispatch: true,
    // Multi-workspace coordination features: Pro and above.
    content_multi_workspace: proPlus,
    // Post image provider BYOK (Fal.ai) — Pro and above.
    content_image_provider_byok: proPlus,
    // MCP Inbound (Excerptus MCP server): Plus and above.
    mcp_inbound: plusPlus,
    // MCP Outbound (Composer → external MCP servers): Plus and above.
    mcp_outbound: plusPlus,
    // Team members / VA invites: Elite and Agency.
    team_members: TEAM_MEMBER_TIERS.includes(tier),
    // @deprecated alias of mcp_inbound — kept so existing callers don't break.
    mcp_access: plusPlus,
  };
};

// Single source of truth. Update this table to change tier behaviour.
export const TIER_ENTITLEMENTS: Record<Tier, TierEntitlements> = {
  starter: {
    excerpts: null,
    downloadsPerMonth: 125,
    workspaces: 1,
    workspaceAddOnMax: 0,
    publishingConnectors: 10,
    aiBundle: "lovable_basic_metered",
    topicTreeColumnCap: 3,
    topicsPerColumnCap: 8,
    affiliateAvailable: true,
    monthlyAiCredits: 75,
    feedSubscriptionsMax: 10,
    podcastSubscriptionsMax: 10,
    feedExtractionsPerDay: 50,
    corpusDocumentCap: 300,
    workspaceMediaBytes: WORKSPACE_MEDIA_BYTES_BY_TIER.starter,
    mcpOutboundPerWorkspace: 0,
    libraryItemsAccountCap: 500,
    libraryItemsPerWorkspaceCap: null,
    teamSeatsMax: 0,
    teamSeatsPerWorkspace: 0,
    capabilities: cap("starter"),
  },
  plus: {
    excerpts: null,
    downloadsPerMonth: 250,
    workspaces: 2,
    workspaceAddOnMax: 0,
    publishingConnectors: 20,
    aiBundle: "lovable_basic",
    topicTreeColumnCap: 4,
    topicsPerColumnCap: 8,
    affiliateAvailable: true,
    monthlyAiCredits: 200,
    feedSubscriptionsMax: 30,
    podcastSubscriptionsMax: 30,
    feedExtractionsPerDay: 150,
    corpusDocumentCap: 1000,
    workspaceMediaBytes: WORKSPACE_MEDIA_BYTES_BY_TIER.plus,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: 1500,
    libraryItemsPerWorkspaceCap: null,
    teamSeatsMax: 0,
    teamSeatsPerWorkspace: 0,
    capabilities: cap("plus"),
  },

  pro: {
    excerpts: null,
    downloadsPerMonth: 500,

    workspaces: 3,
    workspaceAddOnMax: 0,
    publishingConnectors: 30,
    aiBundle: "lovable_plus_premium_capped",
    topicTreeColumnCap: 5,
    topicsPerColumnCap: 8,
    affiliateAvailable: true,
    monthlyAiCredits: 400,
    feedSubscriptionsMax: 50,
    podcastSubscriptionsMax: 50,
    feedExtractionsPerDay: 300,
    corpusDocumentCap: 3000,
    workspaceMediaBytes: WORKSPACE_MEDIA_BYTES_BY_TIER.pro,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: 4500,
    libraryItemsPerWorkspaceCap: null,
    teamSeatsMax: 0,
    teamSeatsPerWorkspace: 0,
    capabilities: cap("pro"),
  },
  elite: {
    excerpts: null,
    downloadsPerMonth: null,
    workspaces: 5,
    workspaceAddOnMax: 4, // up to 9 total
    publishingConnectors: null,
    aiBundle: "byok_required",
    topicTreeColumnCap: 5,
    topicsPerColumnCap: 8,
    affiliateAvailable: true,
    monthlyAiCredits: null,
    feedSubscriptionsMax: 50,
    podcastSubscriptionsMax: 50,
    feedExtractionsPerDay: 300,
    corpusDocumentCap: null,
    workspaceMediaBytes: WORKSPACE_MEDIA_BYTES_BY_TIER.elite,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: null,
    libraryItemsPerWorkspaceCap: 1000,
    teamSeatsMax: 3,
    teamSeatsPerWorkspace: 2,
    capabilities: cap("elite"),
  },
  agency: {
    excerpts: null,
    downloadsPerMonth: null,
    workspaces: 10,
    workspaceAddOnMax: null, // unlimited add-ons
    publishingConnectors: null,
    aiBundle: "byok_required",
    topicTreeColumnCap: 5,
    topicsPerColumnCap: 8,
    affiliateAvailable: true,
    monthlyAiCredits: null,
    feedSubscriptionsMax: 50,
    podcastSubscriptionsMax: 50,
    feedExtractionsPerDay: 500,
    corpusDocumentCap: null,
    workspaceMediaBytes: WORKSPACE_MEDIA_BYTES_BY_TIER.agency,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: null,
    libraryItemsPerWorkspaceCap: 1000,
    teamSeatsMax: null,
    teamSeatsPerWorkspace: 2,
    capabilities: cap("agency"),
  },
  comp: {
    excerpts: null,
    downloadsPerMonth: null,
    workspaces: 3,
    workspaceAddOnMax: 0,
    publishingConnectors: 5,
    aiBundle: "lovable_plus_premium",
    topicTreeColumnCap: 5,
    topicsPerColumnCap: 8,
    affiliateAvailable: false,
    monthlyAiCredits: 400,
    feedSubscriptionsMax: 50,
    podcastSubscriptionsMax: 50,
    feedExtractionsPerDay: 500,
    corpusDocumentCap: 1000,
    workspaceMediaBytes: null,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: null,
    libraryItemsPerWorkspaceCap: null,
    teamSeatsMax: null,
    teamSeatsPerWorkspace: null,
    capabilities: cap("comp"),
  },
  admin_workspace: {
    excerpts: null,
    downloadsPerMonth: null,
    workspaces: null,
    workspaceAddOnMax: null,
    publishingConnectors: null,
    aiBundle: "lovable_plus_premium_byok",
    topicTreeColumnCap: 5,
    topicsPerColumnCap: 8,
    affiliateAvailable: false,
    monthlyAiCredits: 9999,
    feedSubscriptionsMax: 50,
    podcastSubscriptionsMax: 50,
    feedExtractionsPerDay: 500,
    corpusDocumentCap: null,
    workspaceMediaBytes: null,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: null,
    libraryItemsPerWorkspaceCap: null,
    teamSeatsMax: null,
    teamSeatsPerWorkspace: null,
    capabilities: cap("admin_workspace"),
  },
  super_admin: {
    excerpts: null,
    downloadsPerMonth: null,
    workspaces: null,
    workspaceAddOnMax: null,
    publishingConnectors: null,
    aiBundle: "lovable_plus_premium_byok",
    topicTreeColumnCap: 5,
    topicsPerColumnCap: 8,
    affiliateAvailable: false,
    monthlyAiCredits: 9999,
    feedSubscriptionsMax: 50,
    podcastSubscriptionsMax: 50,
    feedExtractionsPerDay: 500,
    corpusDocumentCap: null,
    workspaceMediaBytes: null,
    mcpOutboundPerWorkspace: 10,
    libraryItemsAccountCap: null,
    libraryItemsPerWorkspaceCap: null,
    teamSeatsMax: null,
    teamSeatsPerWorkspace: null,
    capabilities: cap("super_admin"),
  },
};

// Pricing (USD). Yearly = 10× monthly (≈2 months free).
// Locked public prices (intake 2026-04-30). Workspace add-on is monthly-only (no yearly discount).
// Stripe re-pricing for Starter/Plus/Pro tracked in Phase 1 Task 1.3.
export const TIER_PRICING = {
  starter: { monthly: 22, yearly: 220 },
  plus:    { monthly: 33, yearly: 330 },
  pro:     { monthly: 44, yearly: 440 },
  elite:   { monthly: 77, yearly: 770 },
  agency:  { monthly: 147, yearly: 1470 },
} as const;

export const WORKSPACE_ADDON_PRICE = { monthly: 11 } as const;

// Tiers that include a 10-day Stripe free trial when checked out via embedded checkout.
// Trial covers every paid plan; card is still collected at signup (anti-brute-force).
export const TRIAL_ELIGIBLE_TIERS = ["starter", "plus", "pro", "elite", "agency"] as const;
export const TRIAL_DAYS = 10;

// Public-facing display labels. Keep DB/code names internal; show these to users.
export const TIER_DISPLAY_NAMES: Record<Tier, string> = {
  starter: "Starter",
  plus: "Plus",
  pro: "Pro",
  elite: "Elite",
  agency: "Agency",
  comp: "Complimentary",
  admin_workspace: "Admin",
  super_admin: "Super Admin",
};

export const tierDisplayName = (tier: Tier | null | undefined): string =>
  tier ? TIER_DISPLAY_NAMES[tier] : TIER_DISPLAY_NAMES.starter;

export type TierLimits = { excerpts: number | null; downloadsPerMonth: number | null };

const STARTER_FALLBACK: TierEntitlements = TIER_ENTITLEMENTS.starter;

export const tierEntitlements = (tier: Tier | null | undefined): TierEntitlements =>
  tier ? TIER_ENTITLEMENTS[tier] : STARTER_FALLBACK;

export const tierLimits = (tier: Tier | null | undefined): TierLimits => {
  const e = tierEntitlements(tier);
  return { excerpts: e.excerpts, downloadsPerMonth: e.downloadsPerMonth };
};

export const tierWorkspaceLimit = (tier: Tier | null | undefined): number | null =>
  tierEntitlements(tier).workspaces;

export const tierWorkspaceAddOnMax = (tier: Tier | null | undefined): number | null =>
  tierEntitlements(tier).workspaceAddOnMax;

/**
 * Effective workspace cap including paid add-ons.
 * - null = unlimited (Agency, admin_workspace)
 * - Otherwise: base + clamped(addonQty, 0..workspaceAddOnMax)
 */
export const effectiveWorkspaceLimit = (
  tier: Tier | null | undefined,
  addonQty: number | null | undefined,
): number | null => {
  const e = tierEntitlements(tier);
  if (e.workspaces === null) return null;
  const requested = Math.max(0, addonQty ?? 0);
  if (e.workspaceAddOnMax === null) return e.workspaces + requested;
  return e.workspaces + Math.min(requested, e.workspaceAddOnMax);
};

export const tierPublishingConnectorLimit = (tier: Tier | null | undefined): number | null =>
  tierEntitlements(tier).publishingConnectors;


/**
 * Display string for the tier's My Library cap. Used by the pricing grid
 * and per-plan bullets so wording stays consistent everywhere.
 */
export const describeLibraryItemsCap = (tier: Tier | null | undefined): string => {
  const e = tierEntitlements(tier);
  if (e.libraryItemsPerWorkspaceCap !== null) {
    return `${e.libraryItemsPerWorkspaceCap.toLocaleString()} / workspace`;
  }
  if (e.libraryItemsAccountCap !== null) {
    return `${e.libraryItemsAccountCap.toLocaleString()} total`;
  }
  return "Unlimited";
};

/** Per-workspace cap on My Corpus documents. null = unlimited. */
export const tierCorpusDocumentCap = (tier: Tier | null | undefined): number | null =>
  tierEntitlements(tier).corpusDocumentCap;

/** Per-workspace cap on RSS/Atom feed subscriptions in My Feed Reader. */
export const tierFeedSubscriptionLimit = (tier: Tier | null | undefined): number =>
  tierEntitlements(tier).feedSubscriptionsMax;

/**
 * Per-workspace soft daily cap on on-open feed article extractions.
 * MUST stay in sync with EXTRACTIONS_PER_DAY_BY_TIER in
 * supabase/functions/_shared/extractionLimits.ts (server is source of truth).
 */
const FEED_EXTRACTIONS_PER_DAY: Record<string, number> = {
  starter: 50,
  plus: 150,
  pro: 300,
  elite: 300,
  agency: 500,
  comp: 500,
  admin_workspace: 500,
};
export const tierFeedExtractionDailyCap = (tier: Tier | null | undefined): number =>
  FEED_EXTRACTIONS_PER_DAY[tier ?? "starter"] ?? FEED_EXTRACTIONS_PER_DAY.starter;

export const tierAiBundle = (tier: Tier | null | undefined): AiBundle =>
  tierEntitlements(tier).aiBundle;

// Whether the tier can save a BYOK OpenRouter key. Per the cost-rail policy
// (mem://features/ai-cost-rails.md), every paying tier can opt into BYOK —
// it applies to the three configurable AI paths (AI Chat, Planning Agent,
// Writing Agent). Optional on Starter/Plus/Pro; required on Elite/Agency.
const BYOK_ALLOWED_TIERS: ReadonlySet<Tier> = new Set([
  "starter",
  "plus",
  "pro",
  "elite",
  "agency",
  "comp",
  "admin_workspace",
  "super_admin",
]);
export const tierAllowsByok = (tier: Tier | null | undefined): boolean =>
  !!tier && BYOK_ALLOWED_TIERS.has(tier);
export const tierRequiresByok = (tier: Tier | null | undefined): boolean =>
  tier === "elite" || tier === "agency";


export const tierTopicTreeColumnCap = (tier: Tier | null | undefined): number =>
  tierEntitlements(tier).topicTreeColumnCap;

export const tierTopicsPerColumnCap = (tier: Tier | null | undefined): number =>
  tierEntitlements(tier).topicsPerColumnCap;


export const tierAffiliateAvailable = (tier: Tier | null | undefined): boolean =>
  tierEntitlements(tier).affiliateAvailable;

export const tierAllows = (tier: Tier | null | undefined, feature: Feature): boolean =>
  tierEntitlements(tier).capabilities[feature];

export const tierHasCapability = (tier: Tier | null | undefined, capability: Capability): boolean =>
  tierEntitlements(tier).capabilities[capability];

// Public ladder ordering for downgrade selection (lowest → highest).
export const PUBLIC_TIER_LADDER: readonly Tier[] = ["starter", "plus", "pro", "elite", "agency"];

/** Returns the public tiers strictly below the given tier (valid downgrade targets). */
export const downgradeTargets = (currentTier: Tier | null | undefined): Tier[] => {
  if (!currentTier) return [];
  const idx = PUBLIC_TIER_LADDER.indexOf(currentTier);
  if (idx <= 0) return [];
  return PUBLIC_TIER_LADDER.slice(0, idx) as Tier[];
};

/**
 * Reconciliation gate for plan downgrades.
 *
 * A user may only schedule a downgrade once they already fit the target
 * tier's base workspace cap AND have cancelled any add-on seats that the
 * target tier wouldn't allow. Deletion / cancellation happens BEFORE the
 * downgrade is committed, so no workspaces are silently destroyed and no
 * paid add-ons become orphaned.
 *
 * `activeAddons` = add-on seats that are still billing (not yet flagged
 * `cancel_at_period_end`).
 */
export type DowngradeReadiness = {
  ready: boolean;
  workspacesToRemove: number;
  addonsToCancel: number;
  membersToRemove: number;
  targetBaseLimit: number | null;
};

export const downgradeReadiness = (
  targetTier: Tier,
  input: { workspaceCount: number; activeAddons: number; activeMemberCount?: number },
): DowngradeReadiness => {
  const ent = tierEntitlements(targetTier);
  const targetBase = ent.workspaces;
  const targetAddonMax = ent.workspaceAddOnMax ?? 0;
  const targetSeatsMax = ent.teamSeatsMax;

  const workspacesToRemove =
    targetBase === null ? 0 : Math.max(0, input.workspaceCount - targetBase);

  // If the target tier doesn't allow add-ons (max 0), every active seat
  // must be cancelled first. If the target DOES allow add-ons, seats can
  // keep billing on the new plan.
  const addonsToCancel = targetAddonMax === 0 ? input.activeAddons : 0;

  // Team members (active + pending invites). null = unlimited on target.
  const activeMembers = input.activeMemberCount ?? 0;
  const membersToRemove =
    targetSeatsMax === null ? 0 : Math.max(0, activeMembers - targetSeatsMax);

  return {
    ready: workspacesToRemove === 0 && addonsToCancel === 0 && membersToRemove === 0,
    workspacesToRemove,
    addonsToCancel,
    membersToRemove,
    targetBaseLimit: targetBase,
  };
};

/** Account-wide cap on team-member seats (active + pending invites). null = unlimited; 0 = feature off. */
export const tierTeamSeatsMax = (tier: Tier | null | undefined): number | null =>
  tierEntitlements(tier).teamSeatsMax;

/** Per-workspace cap on team-member seats. null = unlimited; 0 = feature off. */
export const tierTeamSeatsPerWorkspace = (tier: Tier | null | undefined): number | null =>
  tierEntitlements(tier).teamSeatsPerWorkspace;

// Tier label for the gate dialog when a feature is locked.
// Plus tier covers branding extras; Pro covers gradients/overlays/readwise.
export const featureTierLabel = (feature: Feature): "Plus" | "Pro" => {
  return feature === "gradient" || feature === "gradient_preset" || feature === "overlay"
    ? "Pro"
    : "Plus";
};

// =====================================================================
// Library / Excerpt workspace mode
// =====================================================================
// Drives whether the My Library and My Excerpts surfaces show workspace
// chips, expose cross-workspace share/move actions, or stay isolated to
// a single workspace. The mode is derived per active workspace from
// (a) the account tier and (b) the workspace's own `library_isolated`
// flag (Elite-only opt-in; Agency forces it on at the data layer).
//
//   - 'single'   : single workspace (Starter / first-launch state) —
//                  no chips, no cross-workspace actions.
//   - 'shared'   : library is pooled across all accessible workspaces;
//                  items can be visible in many at once. Plus / Pro,
//                  and Elite by default.
//   - 'isolated' : the workspace's library is its own silo; items can
//                  only be moved between workspaces, not shared. Agency
//                  by default; Elite per-workspace opt-in.
// =====================================================================

export type LibraryMode = "single" | "shared" | "isolated";

const TIERS_DEFAULT_ISOLATED: readonly Tier[] = ["agency"];
const TIERS_SUPPORT_SHARING: readonly Tier[] = ["plus", "pro", "elite", "comp", "admin_workspace", "super_admin", "agency"];

/**
 * Resolve the library mode for the active workspace.
 *
 * @param tier - Account tier of the primary workspace.
 * @param workspaceCount - Total workspaces the user can access (owned + member).
 * @param workspaceIsolated - The active workspace's `library_isolated` flag.
 */
export const resolveLibraryMode = (
  tier: Tier | null | undefined,
  workspaceCount: number,
  workspaceIsolated: boolean,
): LibraryMode => {
  // Only one workspace in view → no cross-workspace UI is meaningful.
  if (workspaceCount <= 1) return "single";

  // Tier doesn't support cross-workspace library work at all.
  if (!tier || !TIERS_SUPPORT_SHARING.includes(tier)) return "single";

  // Agency: always isolated. Elite: opt-in via the per-workspace flag.
  if (workspaceIsolated) return "isolated";
  if (TIERS_DEFAULT_ISOLATED.includes(tier)) return "isolated";

  return "shared";
};

// Branding rules for the Image Studio "Excerpt Image" tab.
// The "Made with Excerptus.io" attribution line was retired with the free tier;
// only the corner mark remains and is shown on every plan.
export const getCornerMarkMode = (logoPath: string | null | undefined): "default" | "custom" => {
  return logoPath ? "custom" : "default";
};

// ----------------- Gradient helpers (OKLCH interpolation) -----------------

export type GradientStop = { hex: string; pos: number }; // pos 0..100
export type GradientConfig = { angle: number; stops: GradientStop[]; smooth: boolean };

export const DEFAULT_GRADIENT: GradientConfig = {
  angle: 135,
  stops: [
    { hex: "#FBE4D0", pos: 0 },
    { hex: "#F2D7DE", pos: 50 },
    { hex: "#DDE0F4", pos: 100 },
  ],
  smooth: true,
};

const hexToRgb = (hex: string) => {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16) / 255,
    g: parseInt(h.substring(2, 4), 16) / 255,
    b: parseInt(h.substring(4, 6), 16) / 255,
  };
};
const rgbToHex = (r: number, g: number, b: number) => {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
};

// sRGB <-> linear
const sToLin = (v: number) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
const linToS = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);

// linear sRGB -> OKLab (Björn Ottosson)
const rgbLinToOklab = (r: number, g: number, b: number) => {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
};
const oklabToRgbLin = (L: number, a: number, b: number) => {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;
  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
};

const mixOklch = (hexA: string, hexB: string): string => {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  const la = rgbLinToOklab(sToLin(a.r), sToLin(a.g), sToLin(a.b));
  const lb = rgbLinToOklab(sToLin(b.r), sToLin(b.g), sToLin(b.b));
  const mid = oklabToRgbLin((la.L + lb.L) / 2, (la.a + lb.a) / 2, (la.b + lb.b) / 2);
  return rgbToHex(linToS(mid.r), linToS(mid.g), linToS(mid.b));
};

export const gradientToCss = (g: GradientConfig): string => {
  const sorted = [...g.stops].sort((x, y) => x.pos - y.pos);
  let stops = sorted;
  if (g.smooth && sorted.length >= 2) {
    const out: GradientStop[] = [];
    for (let i = 0; i < sorted.length; i++) {
      out.push(sorted[i]);
      if (i < sorted.length - 1) {
        const a = sorted[i];
        const b = sorted[i + 1];
        out.push({ hex: mixOklch(a.hex, b.hex), pos: (a.pos + b.pos) / 2 });
      }
    }
    stops = out;
  }
  const parts = stops.map((s) => `${s.hex} ${s.pos}%`).join(", ");
  return `linear-gradient(${g.angle}deg, ${parts})`;
};

// Perceived luminance (0..1) for a single hex color.
export const hexLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

// Average luminance of gradient stops (0..1).
export const gradientAvgLuminance = (g: GradientConfig): number => {
  if (!g.stops.length) return 1;
  let sum = 0;
  for (const s of g.stops) sum += hexLuminance(s.hex);
  return sum / g.stops.length;
};

// Effective luminance after compositing a flat overlay (color + opacity 0..1) on top of the base.
export const effectiveLuminance = (baseLum: number, overlayHex: string, overlayOpacity: number): number => {
  const o = Math.max(0, Math.min(1, overlayOpacity));
  return baseLum * (1 - o) + hexLuminance(overlayHex) * o;
};
